/**
 * Employee change requests — an employee asking HR to add or update a record
 * in one of their own sections (document, education, family, bank account,
 * personal information).
 *
 * Both portals share this contract: the employee portal raises and withdraws
 * requests, the company portal lists and decides them. It is the API-level
 * counterpart of the display-oriented `detail-update-requests` contract that
 * the shared request cards render.
 */

import type { OptionalPageLimit } from './common';
import type { ApiResponse, ExtraFields, GetListResponse } from './generic';
import type { EmployeeEducationFormValues } from './employee';

export enum EmployeeChangeRequestStatus {
  Pending = 'pending',
  Approved = 'approved',
  Rejected = 'rejected',
  Withdrawn = 'withdrawn',
  /**
   * Saved to the record at once, with no reviewer: the company's change rule
   * for the item is notify HR, notify the manager or none.
   */
  Applied = 'applied',
}

/**
 * The single-choice change rule of 2026-10-21 (go-hr #2098), before the rule
 * became two columns. Still sent as the closest single value (`mode`,
 * `effectiveMode`, `changeMode`, `approvalMode`); read `ChangeRulePartyMode`
 * columns for what actually happens.
 */
export enum ChangeMode {
  HrApproval = 'hr_approval',
  ManagerApproval = 'manager_approval',
  NotifyHr = 'notify_hr',
  NotifyManager = 'notify_manager',
  None = 'none',
}

/**
 * The items a company sets a change rule for. `address` and
 * `emergency_contact` exist for the rule only: on the request side they are
 * part of personal information and of family.
 */
export enum ChangeRuleItem {
  Address = 'address',
  Family = 'family',
  EmergencyContact = 'emergency_contact',
  PersonalInformation = 'personal_information',
  Education = 'education',
  Document = 'document',
  BankAccount = 'bank_account',
}

/** Who decides a pending request, or was told about an applied one. */
export enum ChangeRequestRoute {
  Hr = 'hr',
  Manager = 'manager',
}

/**
 * What one party (HR, or the employee's direct manager) does when an
 * employee changes an item of their file (Settings > Employees > Change
 * rules, two columns). Both approve: the manager first, then HR.
 */
export enum ChangeRulePartyMode {
  Approve = 'approve',
  Notify = 'notify',
  None = 'none',
}

/** One item's rule as the settings page reads and saves it. */
export interface ChangeRuleSetting {
  section: ChangeRuleItem;
  hrMode: ChangeRulePartyMode;
  managerMode: ChangeRulePartyMode;
  /** The closest single value (read-only). */
  approvalMode: ChangeMode;
  /** Bank details: the HR column is always approve, shown disabled. */
  locked: boolean;
  /** Employees may change the item (Create or Update on its grid row). */
  editable?: boolean;
  updatedByUserId?: string | null;
  updatedAt?: string | null;
}

export interface ChangeRuleSettings {
  companyId: string;
  settings: ChangeRuleSetting[];
}

export interface UpdateChangeRulePayload {
  section: ChangeRuleItem;
  hrMode: ChangeRulePartyMode;
  managerMode: ChangeRulePartyMode;
}

/**
 * `GET /employee/change-requests/modes`: what each item does for the
 * signed-in employee. A manager rule without an eligible direct manager is
 * already turned into its HR twin in `effectiveMode`.
 */
export interface EffectiveChangeMode {
  item: ChangeRuleItem;
  mode: ChangeMode;
  effectiveMode: ChangeMode;
  /** Label the button "Save" (true) or "Send for approval" (false). */
  appliesImmediately: boolean;
  /** Who decides first (pending) or is told first (applied); or null. */
  recipient: ChangeRequestRoute | null;
  locked: boolean;
  /** False: employees cannot change the item at all; hide its rule. */
  editable?: boolean;
  hrMode: ChangeRulePartyMode;
  managerMode: ChangeRulePartyMode;
  /** The two columns for this employee (no manager: HR approves instead). */
  effectiveHrMode: ChangeRulePartyMode;
  effectiveManagerMode: ChangeRulePartyMode;
  /** Who decides, in order; empty when the change is saved at once. */
  steps: ChangeRequestRoute[];
  /** Who is told once the change is saved. */
  notified: ChangeRequestRoute[];
}

export interface EmployeeChangeModes {
  hasManager: boolean;
  items: EffectiveChangeMode[];
}

/** One row a submission wrote, as the submit response lists it. */
export interface ChangeRequestSubmitResult {
  id: string;
  status: EmployeeChangeRequestStatus;
  changeMode: ChangeMode;
  ruleItem: ChangeRuleItem;
  routedTo: ChangeRequestRoute | null;
  /** The request fields this row carries. */
  fields: string[];
  hrMode?: ChangeRulePartyMode;
  managerMode?: ChangeRulePartyMode;
  /** Pending: who decides, in order. */
  steps?: ChangeRequestRoute[];
  notified?: ChangeRequestRoute[];
}

/** Sections that accept change requests. */
export enum EmployeeSection {
  Document = 'document',
  Education = 'education',
  Family = 'family',
  BankAccount = 'bank_account',
  PersonalInformation = 'personal_information',
}

export enum EmployeeChangeRequestAction {
  Create = 'create',
  Update = 'update',
  Delete = 'delete',
}

/** The employee's justification, carried on every create/update body. */
export interface ReasonForChangePayload {
  /** Max 1000 characters, enforced server-side. */
  reasonForChange?: string;
}

export interface EmployeeDocumentChangeFields {
  documentTypeId: string;
  /** Optional server-side: an update that only re-files a document sends none. */
  documentUrl?: string;
  /**
   * `YYYY-MM-DD`, the day the document stops being valid. `null` on an update
   * asks for the date on the record to be cleared; leaving it out keeps it.
   */
  expiresOn?: string | null;
  extraData?: ExtraFields | null;
}

export type CreateDocumentChangeRequestPayload = EmployeeDocumentChangeFields &
  ReasonForChangePayload;

export type UpdateDocumentChangeRequestPayload =
  Partial<EmployeeDocumentChangeFields> & ReasonForChangePayload;

export type CreateEducationChangeRequestPayload = EmployeeEducationFormValues &
  ReasonForChangePayload;

export type UpdateEducationChangeRequestPayload =
  Partial<EmployeeEducationFormValues> & ReasonForChangePayload;

/**
 * The wire shape of a family request, which is not the record shape: every
 * value is a string, and an optional the employee left blank is omitted rather
 * than sent empty — the API validates an optional only when its key is present.
 */
export interface EmployeeFamilyChangeFields {
  name: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  isEmergencyContact?: boolean;
  relation: string;
  gender: string;
  email?: string;
  /** ISO `YYYY-MM-DD`. */
  dateOfBirth?: string;
}

export type CreateFamilyChangeRequestPayload = EmployeeFamilyChangeFields &
  ReasonForChangePayload;

export type UpdateFamilyChangeRequestPayload =
  Partial<EmployeeFamilyChangeFields> & ReasonForChangePayload;

/** One country-specific banking value, e.g. an institution or transit number. */
export interface BankFieldValuePayload {
  countryBankingFieldId: string;
  fieldValue: string;
}

/**
 * The wire shape of a bank account request: the same fields HR enters, plus
 * an optional void cheque. The cheque is an S3 key under
 * `employee/void-cheques/` from the presigned upload flow; it rides on the
 * request only, so HR can check it before approving.
 */
export interface EmployeeBankAccountChangeFields {
  countryCode: string;
  bankName: string;
  accountHolderName: string;
  accountNumber: string;
  fields: BankFieldValuePayload[];
  voidChequeUrl?: string;
}

export type CreateBankAccountChangeRequestPayload =
  EmployeeBankAccountChangeFields & ReasonForChangePayload;

/** The country is fixed on the account (its banking fields depend on it). */
export type UpdateBankAccountChangeRequestPayload = Partial<
  Omit<EmployeeBankAccountChangeFields, 'countryCode'>
> &
  ReasonForChangePayload;

/**
 * The employee's permanent address, as the personal information route takes
 * it. Every part is optional; null clears it.
 */
export interface PersonalInformationAddressPayload {
  addressLine_1?: string | null;
  addressLine_2?: string | null;
  city?: string | null;
  provinceId?: number | null;
  countryCode?: string | null;
  postalCode?: string | null;
}

/**
 * A personal information request. Every field is optional and only the ones
 * sent are changed on approval. The sign-in email is never part of it: the
 * API refuses it (decision D57).
 */
export interface EmployeePersonalInformationChangeFields {
  personalEmail?: string | null;
  phoneNumber?: string | null;
  /** ISO `YYYY-MM-DD`. */
  gender?: string;
  address?: PersonalInformationAddressPayload;
  shirtSize?: string;
  pantsSize?: string;
  pantsLength?: string;
}

export type CreatePersonalInformationChangeRequestPayload =
  EmployeePersonalInformationChangeFields & ReasonForChangePayload;

/**
 * One field the request touches, as the list endpoints report it.
 *
 * `before` is the live record's value captured at submission time, so it only
 * carries meaning on an update — a create has nothing to have changed from and
 * always sends `null`.
 */
export interface EmployeeChangeRequestFieldChange {
  field: string;
  before: unknown;
  after: unknown;
}

/** Reviewer identity, attached once the request has been decided. */
export interface EmployeeChangeRequestReviewer {
  id: string;
  name: string;
}

/**
 * Employee identity attached to a request in the company-portal list. The API
 * joins the name itself and sends it whole, not as separate name parts.
 */
export interface EmployeeChangeRequestEmployee {
  id: string;
  name: string;
}

export interface EmployeeChangeRequest {
  id: string;
  companyId: string;
  employeeId: string;
  section: EmployeeSection;
  action: EmployeeChangeRequestAction;
  status: EmployeeChangeRequestStatus;
  /** The record being updated; `null` on a create request. */
  recordId: string | null;
  /**
   * The field-level diff behind the request — the only shape the values come
   * back in; there is no separate requested/current blob to compare by hand.
   */
  changes: EmployeeChangeRequestFieldChange[];
  reasonForChange?: string | null;
  /** The reviewer's rejection reason, present once a request is rejected. */
  reason?: string | null;
  /** Free-form reviewer note, optional on both approve and reject. */
  reviewerNote?: string | null;
  reviewedBy?: EmployeeChangeRequestReviewer | null;
  reviewedAt?: string | null;
  /** When the employee submitted the request. */
  submittedAt: string;
  employee?: EmployeeChangeRequestEmployee;
  /** The change rule that applied; null on requests from before 2026-10-21. */
  changeMode?: ChangeMode | null;
  ruleItem?: ChangeRuleItem | null;
  /** Pending: who decides it now. Applied: who was told (HR when both). */
  routedTo?: ChangeRequestRoute | null;
  /** The rule's two columns as they applied (HR approval on older rows). */
  hrMode?: ChangeRulePartyMode;
  managerMode?: ChangeRulePartyMode;
  /** Who is (or was) told once the change is saved. */
  notified?: ChangeRequestRoute[];
  /**
   * Both approve: the manager's approval (step 1 of 2), after which the
   * request waits for HR. Null on a one-step request.
   */
  managerApproval?: {
    approvedAt: string;
    approvedBy: EmployeeChangeRequestReviewer | null;
    note: string | null;
  } | null;
  /**
   * Submit responses only: every row the submission wrote. A personal
   * information change whose address and other fields follow different
   * rules writes two (one applied, one pending, say).
   */
  results?: ChangeRequestSubmitResult[];
}

/**
 * Filters for the request list. With no `status` every status comes back;
 * pass `section` + `recordId` to pull one record's full timeline.
 */
export interface GetEmployeeChangeRequestsParams extends OptionalPageLimit {
  status?: EmployeeChangeRequestStatus;
  section?: EmployeeSection;
  recordId?: string;
}

/** The company-portal list additionally narrows to one employee. */
export interface GetCompanyChangeRequestsParams
  extends GetEmployeeChangeRequestsParams {
  employeeId?: string;
}

export type GetEmployeeChangeRequestsResponse =
  GetListResponse<EmployeeChangeRequest>;

export type EmployeeChangeRequestResponse = ApiResponse<EmployeeChangeRequest>;

/**
 * Pending change request counts for one employee. `bySection` is zero-filled
 * for every section this feature supports, so an absent key is a section that
 * accepts no requests at all — not one that merely has nothing pending.
 */
export interface EmployeeChangeRequestSummary {
  total: number;
  bySection: Partial<Record<EmployeeSection, number>>;
}

export type GetEmployeeChangeRequestSummaryResponse =
  ApiResponse<EmployeeChangeRequestSummary>;

export interface RejectChangeRequestPayload {
  /** Required and non-empty — the API answers 400 otherwise. */
  reason: string;
}
